import axios from "axios";
import { useEffect, useState, useRef, memo } from "react";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { MdFileDownload } from "react-icons/md";
import { IoSearch } from "react-icons/io5";
import { motion } from "framer-motion";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import backarrow from "../assets/icon/arrow-right.svg";
import {
  FaUser,
  FaMoneyBillWave,
  FaCoins,
  FaFileExcel,
  FaSearch,
} from "react-icons/fa";

const AnimatedBackground = memo(() => {
  const icons = [FaUser, FaMoneyBillWave, FaCoins, FaFileExcel, FaSearch];
  const rows = 5,
    cols = 11;
  const iconArray = [];

  for (let i = 0; i < rows; i++)
    for (let j = 0; j < cols; j++) {
      const Icon = icons[Math.floor(Math.random() * icons.length)];
      iconArray.push({
        Icon,
        size: 18 + Math.random() * 20,
        top: (i / rows) * 100 + Math.random() * 5,
        left: (j / cols) * 100 + Math.random() * 5,
        duration: 15 + Math.random() * 10,
        opacity: 0.15 + Math.random() * 0.25,
        xOffset: Math.random() * 10 - 5,
        yOffset: Math.random() * 10 - 5,
      });
    }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {iconArray.map((item, i) => (
        <motion.div
          key={i}
          className="absolute text-white/50"
          style={{
            top: `${item.top}%`,
            left: `${item.left}%`,
            opacity: item.opacity,
          }}
          initial={{ scale: 0.5 }}
          animate={{
            x: [0, item.xOffset, 0],
            y: [0, item.yOffset, 0],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration: item.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <item.Icon size={item.size} />
        </motion.div>
      ))}
    </div>
  );
});

const DebtorsList = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [loading, setLoading] = useState(true);
  const [allDebtors, setAllDebtors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const searchRef = useRef("");
  const debounceRef = useRef(null);
  const location = useLocation();
  const courseId = location.state?.courseId;
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!courseId) {
      navigate("/Mainpage/ListOfDebtors");
    }
  }, [courseId, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchDebtors = async (pageNumber = 1) => {
    if (!courseId) return;
    setLoading(true);
    const limit = windowHeight < 1000 ? 5 : 10;

    try {
      const res = await axios.get(
        `${API_BASE}/api/Financial/List_Of_Debtors_By_CourseId/${courseId}`,
        {
          params: {
            page: pageNumber,
            limit,
            search: searchRef.current.trim(),
          },
          headers: { Authorization: localStorage.getItem("token") },
        }
      );

      if (res.status === 200) {
        const data = res.data.data.map((item) => ({
          studentId: item.studentId,
          firstName: item.firstName,
          lastName: item.lastName,
          Totalamount: item.Totalamount || 0,
          Amountpaid: item.Amountpaid || 0,
          Unpaidamount: item.Unpaidamount || 0,
        }));
        setAllDebtors(data);
        setTotalPages(res.data.pagination.totalPages);
        setPage(res.data.pagination.currentPage);
      }
    } catch (error) {
      toast.error("خطا در دریافت دانشجویان. لطفاً دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebtors(page);
  }, [page, windowHeight, courseId]);

  const handleSearchChange = (value) => {
    searchRef.current = value;
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchDebtors(1);
    }, 500);
  };

  const handleDownload = async () => {
    if (!courseId) return;
    try {
      const res = await axios.get(
        `${API_BASE}/api/financial/DebtorsExcel/${courseId}`,
        {
          responseType: "blob",
          headers: { Authorization: localStorage.getItem("token") },
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Debtors_${courseId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("فایل اکسل با موفقیت دانلود شد");
    } catch (error) {
      toast.error("خطا در دانلود فایل اکسل");
    }
  };

  // صفحه‌بندی هوشمند (۲ یا ۳ دکمه بسته به عرض صفحه)
  const visiblePagesCount = windowWidth < 768 ? 2 : 3;
  const halfVisible = Math.floor(visiblePagesCount / 2);
  let startPage = Math.max(1, page - halfVisible);
  let endPage = Math.min(totalPages, startPage + visiblePagesCount - 1);
  if (endPage - startPage + 1 < visiblePagesCount)
    startPage = Math.max(1, endPage - visiblePagesCount + 1);

  if (!localStorage.getItem("token")) return <Navigate to="/" />;

  return (
    <div className="relative min-h-screen w-full bg-gray-800 flex items-center justify-center p-4">
      <AnimatedBackground />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-3xl mx-auto border border-white/30 overflow-hidden mt-11"
      >
        {/* هدر */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <img
            src={backarrow}
            alt="back"
            className="w-8 h-8 cursor-pointer hover:scale-110 transition"
            onClick={() => navigate("/Mainpage/ListOfDebtors")}
          />
          <h2 className="text-2xl font-bold text-gray-800">لیست بدهکاران</h2>
          <div className="w-8" />
        </div>

        {/* سرچ و دانلود */}
        <div className="p-6 pt-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:max-w-[320px]">
              <input
                type="text"
                placeholder="جستجو بر اساس نام و نام خانوادگی ..."
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-md border-2 border-gray-300 focus:border-gray-700 outline-none text-gray-800 font-iranSansNormal transition"
              />
              <IoSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDownload}
              className="flex items-center gap-2 px-5 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-md transition font-iranSansNormal"
            >
              <MdFileDownload size={20} />
              دانلود اکسل
            </motion.button>
          </div>
        </div>

        {/* جدول */}
        <div className="px-6 pb-6">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-xl font-bold text-gray-600">
                در حال بارگذاری ...
              </p>
            </div>
          ) : allDebtors.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl font-bold text-gray-600">
                بدهکاری یافت نشد!
              </p>
            </div>
          ) : (
            <div className="rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-800 text-white font-iranSansNormal">
                  <tr>
                    <th className="py-4 px-6 text-right">نام</th>
                    <th className="py-4 px-6 text-right">نام خانوادگی</th>
                    <th className="py-4 px-6 text-right">مبلغ کل</th>
                    <th className="py-4 px-6 text-right">پرداخت شده</th>
                    <th className="pr-14 py-4 px-6 text-right">باقی مانده</th>
                  </tr>
                </thead>
                <tbody>
                  {allDebtors.map((item, idx) => (
                    <motion.tr
                      key={item.studentId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="border-b border-gray-300 hover:bg-gray-100 transition"
                    >
                      <td className="py-4 px-6 text-base font-iranSansNormal text-gray-800">
                        {item.firstName}
                      </td>
                      <td className="py-4 px-6 text-base font-iranSansNormal text-gray-800">
                        {item.lastName}
                      </td>
                      <td className="py-4 px-6 text-base font-iranSansNormal text-gray-800">
                        {item.Totalamount.toLocaleString()} تومان
                      </td>
                      <td className="py-4 px-6 text-base font-medium text-green-600">
                        {item.Amountpaid.toLocaleString()} تومان
                      </td>
                      <td className="py-4 px-6 text-base font-bold text-red-600">
                        {item.Unpaidamount.toLocaleString()} تومان
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* صفحه‌بندی — دقیقاً مثل AllCourses، در پایین کارت */}
        {totalPages > 1 && !loading && (
          <div className="flex justify-center items-center gap-3 mt-8 pb-8">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-full disabled:opacity-50 hover:bg-gray-200 transition"
            >
              <FaArrowRight size={20} />
            </button>

            {Array.from(
              { length: endPage - startPage + 1 },
              (_, i) => startPage + i
            ).map((pageNum) => (
              <motion.button
                key={pageNum}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setPage(pageNum)}
                className={`w-8 h-8 text-sm font-medium rounded-md transition ${
                  pageNum === page
                    ? "bg-gray-800 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {pageNum}
              </motion.button>
            ))}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-full disabled:opacity-50 hover:bg-gray-200 transition"
            >
              <FaArrowLeft size={20} />
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default DebtorsList;
